function SideMenu(el) {
    this.el = el;
    this.menuItemWrapper = this.el.querySelector('.jsSideMenuItems');
    this.rightEl = this.el.querySelector('.jsSideMenuRight');
    this.backBtn = this.rightEl.querySelector('.jsSideMenuBack');
    this.featuredEl = this.el.querySelector('.jsSideMenuFeatured');
    this.triggerEl = null;
    this.submenuIdCounter = 0;

    this.initTabs();
    this.initAccordions();
    this.initTopLevelTriggers();
    this.bindKeyboard();

    this.menuItemWrapper.addEventListener('click', e => {
        const menuItem = e.target.closest('.menu > .menu-item-has-children');
        if (!menuItem || !this.menuItemWrapper.contains(menuItem)) return;

        const link = menuItem.querySelector(':scope > a');
        if (!link || (e.target !== link && !link.contains(e.target))) return;

        e.preventDefault();
        this.activateTab(menuItem.dataset.tabId);
    });

    this.tabWrapper.addEventListener('click', e => {
        const menuItem = e.target.closest('.tab__menu > .menu-item-has-children');
        if (!menuItem) return;

        // Don't intercept clicks on nested submenu links
        const nestedList = menuItem.querySelector(':scope > ul');
        if (nestedList && nestedList.contains(e.target)) return;

        e.preventDefault();
        this.toggleAccordion(menuItem);
    });

    this.el.addEventListener('click', e => {
        if (e.target.classList.contains('jsSideMenuClose')) {
            this.hide();
        }
    });

    this.backBtn.addEventListener('click', e => {
        e.preventDefault();

        this.rightEl.classList.remove('active');
        if (this.featuredEl) this.featuredEl.classList.remove('to-bottom');

        const activeTab = this.tabWrapper.querySelector('.tab.active');
        if (activeTab) {
            this.resetNestedOpenStates(activeTab);
            activeTab.classList.remove('active');
        }

        const openMenuItem = this.menuItemWrapper.querySelector('li.open');
        if (openMenuItem) {
            openMenuItem.classList.remove('open');
            this.setTopLevelExpanded(openMenuItem, false);
        }

        const firstTopLink = this.menuItemWrapper.querySelector('.menu > li > a');
        if (firstTopLink) firstTopLink.focus();
    });
};

SideMenu.prototype.getFocusableElements = function() {
    const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const isMobile = window.matchMedia('(max-width: 479px)').matches;
    const rightActive = this.rightEl.classList.contains('active');

    return [...this.el.querySelectorAll(selector)].filter(el => {
        if (el.closest('[hidden], [aria-hidden="true"]')) return false;

        // On mobile the right panel slides over; keep focus in the visible panel
        if (isMobile) {
            if (rightActive && !this.rightEl.contains(el)) return false;
            if (!rightActive && this.rightEl.contains(el)) return false;
        }

        const tab = el.closest('.tabs .tab');
        if (tab && !tab.classList.contains('active')) return false;

        const accordion = el.closest('.tab__menu > .menu-item-has-children');
        if (accordion) {
            const nestedList = accordion.querySelector(':scope > ul');
            if (nestedList && nestedList.contains(el) && !accordion.classList.contains('open')) {
                return false;
            }
        }

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        return el.getClientRects().length > 0;
    });
};

SideMenu.prototype.bindKeyboard = function() {
    this.el.addEventListener('keydown', e => {
        if (!document.body.classList.contains('side-menu-open')) return;

        if (e.key === 'Escape') {
            e.preventDefault();

            // Close open accordion first, then right panel, then the menu
            const openAccordion = this.tabWrapper.querySelector('.tab.active .tab__menu > .menu-item-has-children.open');
            if (openAccordion) {
                this.toggleAccordion(openAccordion, false);
                const link = openAccordion.querySelector(':scope > a');
                if (link) link.focus();
                return;
            }

            if (this.rightEl.classList.contains('active') && window.matchMedia('(max-width: 479px)').matches) {
                this.backBtn.click();
                return;
            }

            this.hide();
            return;
        }

        if (e.key !== 'Tab') return;

        const focusables = this.getFocusableElements();
        if (focusables.length < 2) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    });
};

SideMenu.prototype.setTopLevelExpanded = function(menuItem, isOpen) {
    const link = menuItem.querySelector(':scope > a');
    if (!link) return;
    link.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
};

SideMenu.prototype.initTopLevelTriggers = function() {
    [...this.menuItemWrapper.querySelectorAll('.menu > .menu-item-has-children')].forEach(item => {
        const link = item.querySelector(':scope > a');
        if (!link) return;

        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-haspopup', 'true');

        link.addEventListener('keydown', e => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            this.activateTab(item.dataset.tabId);
        });
    });
};

SideMenu.prototype.initAccordions = function() {
    [...this.tabWrapper.querySelectorAll('.tab__menu > .menu-item-has-children')].forEach(item => {
        const link = item.querySelector(':scope > a');
        const submenu = item.querySelector(':scope > ul');
        if (!link || !submenu) return;

        this.submenuIdCounter += 1;
        if (!submenu.id) submenu.id = 'side-submenu-' + this.submenuIdCounter;

        link.setAttribute('role', 'button');
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-controls', submenu.id);

        link.addEventListener('keydown', e => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            this.toggleAccordion(item);
        });
    });
};

SideMenu.prototype.toggleAccordion = function(menuItem, forceOpen) {
    const wasOpen = menuItem.classList.contains('open');
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : !wasOpen;

    this.resetNestedOpenStates(menuItem.closest('.tab__menu'));

    if (shouldOpen) menuItem.classList.add('open');

    const link = menuItem.querySelector(':scope > a');
    if (link) link.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
};

SideMenu.prototype.show = function(triggerEl) {
    this.triggerEl = triggerEl || document.activeElement;
    document.body.classList.add('side-menu-open');
    this.el.setAttribute('aria-hidden', 'false');

    if (this.triggerEl && this.triggerEl.setAttribute) {
        this.triggerEl.setAttribute('aria-expanded', 'true');
    }

    gsap.timeline()
        .set(this.el, { opacity: 0 })
        .add(_ => this.el.classList.add('visible'))
        .to(this.el, 0.5, { opacity: 1, ease: 'power2.inOut' })
        .add(_ => {
            const closeBtn = this.el.querySelector('.jsSideMenuClose');
            if (closeBtn) closeBtn.focus();
        })
    ;
};

SideMenu.prototype.hide = function() {
    gsap.timeline()
        .to(this.el, 0.5, { opacity: 0, ease: 'power2.inOut' })
        .add(_ => {
            this.rightEl.classList.remove('active');
            if (this.featuredEl) this.featuredEl.classList.remove('to-bottom');

            this.el.classList.remove('visible');
            this.el.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('side-menu-open');

            const activeTab = this.tabWrapper.querySelector('.tab.active');
            if (activeTab) {
                this.resetNestedOpenStates(activeTab);
                activeTab.classList.remove('active');
            }

            const openMenuItem = this.menuItemWrapper.querySelector('li.open');
            if (openMenuItem) {
                openMenuItem.classList.remove('open');
                this.setTopLevelExpanded(openMenuItem, false);
            }

            if (this.triggerEl && this.triggerEl.setAttribute) {
                this.triggerEl.setAttribute('aria-expanded', 'false');
            }

            if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
                this.triggerEl.focus();
            }

            this.triggerEl = null;
        })
    ;
};

SideMenu.prototype.initTabs = function() {
    this.tabWrapper = this.el.querySelector('.jsTabs');
};

SideMenu.prototype.resetNestedOpenStates = function(scope) {
    if (!scope) return;
    [...scope.querySelectorAll('.tab__menu > .menu-item-has-children')].forEach(item => {
        item.classList.remove('open');
        const link = item.querySelector(':scope > a');
        if (link) link.setAttribute('aria-expanded', 'false');
    });
};

SideMenu.prototype.activateTab = function(tabId) {
    this.rightEl.classList.add('active');
    if (this.featuredEl) this.featuredEl.classList.add('to-bottom');

    const activeTab = this.tabWrapper.querySelector('.tab.active');
    if (activeTab) {
        this.resetNestedOpenStates(activeTab);
        activeTab.classList.remove('active');
    }

    const nextActiveTab = this.tabWrapper.querySelector('.tab[data-tab-id="' + tabId + '"]');
    if (nextActiveTab) {
        this.resetNestedOpenStates(nextActiveTab);
        nextActiveTab.classList.add('active');
    }

    const openMenuItem = this.menuItemWrapper.querySelector('li.open');
    if (openMenuItem) {
        openMenuItem.classList.remove('open');
        this.setTopLevelExpanded(openMenuItem, false);
    }

    const nextOpenMenuItem = this.menuItemWrapper.querySelector('li[data-tab-id="' + tabId + '"]');
    if (nextOpenMenuItem) {
        nextOpenMenuItem.classList.add('open');
        this.setTopLevelExpanded(nextOpenMenuItem, true);
    }

    // Move focus into the opened panel
    const focusTarget = this.backBtn.offsetParent !== null
        ? this.backBtn
        : (nextActiveTab && nextActiveTab.querySelector('a, button'));

    if (focusTarget) focusTarget.focus();
};

(function() {
    const sideMenuEl = document.querySelector('.jsSideMenu');
    if (sideMenuEl) {
        const sideMenu = new SideMenu(sideMenuEl);
        sideMenuEl.setAttribute('aria-hidden', 'true');

        document.body.addEventListener('click', e => {
            const trigger = e.target.closest('.jsShowSideMenuTrigger');
            if (!trigger) return;

            e.preventDefault();
            sideMenu.show(trigger);
        });
    }
})();
